import SocialLogin from '@/components/Auth/SocialLogin';
import { Button } from '@/components/Button';
import { FormCard } from '@/components/FormCard';
import { GradientText } from '@/components/GradientText';
import { Link } from '@/components/Link';
import { PasswordInput } from '@/components/PasswordInput';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { AuthLayout } from '@/layouts/AuthLayout';
import { useAuthStore } from '@/stores/useAuthStore';
import type { EmailLoginCredentials } from '@/types/auth';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { t } from 'i18next';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';
export const SignIn = () => {
  const { emailLogin } = useAuthStore();
  const location = useLocation();

  const initialValues: EmailLoginCredentials = {
    email: '',
    password: '',
  };

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t('common:validation.email.format'))
      .required(t('common:validation.email.required')),
    password: yup.string().required(t('common:validation.password.required')),
  });

  const handleSignIn = async (
    values: EmailLoginCredentials,
    { setSubmitting, setErrors }: FormikHelpers<EmailLoginCredentials>,
  ) => {
    try {
      await emailLogin(values);
    } catch (error: unknown) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t('signIn:welcome')}
      welcomeText={t('signIn:welcome-text')}
    >
      <FormCard title={t('signIn:connect-to-account')}>
        {location.state?.from && (
          <div className="mb-4 rounded-lg bg-green-50 p-4 text-sm text-green-700">
            {t('accessory:login-or-register-before')}
          </div>
        )}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSignIn}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="w-full space-y-4 md:space-y-5">
              <div className="space-y-2">
                <TextInput
                  label={t('signIn:label.email')}
                  name="email"
                  type="email"
                  placeholder={t('signIn:placeholder.email')}
                  error={!!touched.email && !!errors.email}
                />
              </div>

              <div className="space-y-2">
                <PasswordInput
                  label={t('signIn:label.password')}
                  name="password"
                  placeholder={t('signIn:placeholder.password')}
                  error={!!touched.password && !!errors.password}
                />
                <div className="flex items-center justify-end">
                  <Link
                    to="forgot-password"
                    className="text-xs font-medium text-sky-600 hover:text-sky-800"
                  >
                    {t('signIn:forgot-password')}
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                loading={isSubmitting}
                className="mt-4 !w-full bg-gradient-to-r from-sky-600 to-sky-500 py-3 text-white shadow-md hover:from-sky-700 hover:to-sky-600 focus:ring-2 focus:ring-sky-300 md:mt-6"
              >
                {t('signIn:submit')}
              </Button>
            </Form>
          )}
        </Formik>
      </FormCard>

      <SocialLogin />

      <Text className="mt-6 text-center text-sm text-gray-600 md:mt-8">
        {t('signIn:sign-up.description')}{' '}
        <Link
          to="sign-up"
          state={{ from: location.state?.from }}
          className="font-medium text-sky-600 hover:text-sky-800"
        >
          <GradientText>{t('signIn:sign-up.link')}</GradientText>
        </Link>
      </Text>
    </AuthLayout>
  );
};
